import React, { useState } from 'react';
import { Info, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { getFieldExplanation, formatExplanation, STATUS_TYPES } from '@/utils/missingDataExplanations';

/**
 * Missing Data Explanation Component
 * 
 * A lightweight, reusable component for displaying explanations
 * when analytical fields are empty or unavailable.
 * 
 * Props:
 * - fieldId: string - The field identifier from FIELD_EXPLANATIONS
 * - conditionKey: string - The specific condition key
 * - fieldName: string - Display name for the field (optional)
 * - compact: boolean - Use compact inline display (default: false)
 * - showIcon: boolean - Show info/alert icon (default: true)
 * - expandable: boolean - Allow expanding details (default: true)
 * - className: string - Additional CSS classes
 */
export const MissingDataExplanation = ({
  fieldId,
  conditionKey,
  fieldName = null,
  compact = false,
  showIcon = true,
  expandable = true,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const explanation = getFieldExplanation(fieldId, conditionKey);
  if (!explanation) return null;
  
  const formatted = formatExplanation(explanation);
  
  // Status-based styling
  const statusStyles = {
    [STATUS_TYPES.UNAVAILABLE]: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      text: 'text-gray-600',
      icon: 'text-gray-400',
    },
    [STATUS_TYPES.NOT_YET_AVAILABLE]: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      icon: 'text-blue-400',
    },
    [STATUS_TYPES.NOT_APPLICABLE]: {
      bg: 'bg-slate-50',
      border: 'border-slate-200',
      text: 'text-slate-600',
      icon: 'text-slate-400',
    },
    [STATUS_TYPES.INSUFFICIENT_EVIDENCE]: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: 'text-amber-400',
    },
    [STATUS_TYPES.PARTIALLY_AVAILABLE]: {
      bg: 'bg-cyan-50',
      border: 'border-cyan-200',
      text: 'text-cyan-700',
      icon: 'text-cyan-400',
    },
  };
  
  const style = statusStyles[explanation.status] || statusStyles[STATUS_TYPES.UNAVAILABLE];

  // Compact inline display
  if (compact) {
    return (
      <span 
        className={`inline-flex items-center gap-1 text-xs ${style.text} ${className}`}
        title={`${explanation.reason}\nRequired: ${formatted.requirements}\nHow to rectify: ${formatted.rectify}`}
        data-testid={`missing-data-compact-${fieldId}`}
      >
        {showIcon && <Info className={`w-3 h-3 ${style.icon}`} />}
        <span>{formatted.compact}</span>
      </span>
    );
  }

  // Full display with optional expansion
  return (
    <div 
      className={`${style.bg} ${style.border} border rounded-lg p-3 ${className}`}
      data-testid={`missing-data-${fieldId}`}
    >
      {/* Header */}
      <div 
        className={`flex items-center justify-between ${expandable ? 'cursor-pointer' : ''}`}
        onClick={expandable ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <div className="flex items-center gap-2">
          {showIcon && <AlertCircle className={`w-4 h-4 ${style.icon}`} />}
          <div>
            {fieldName && (
              <p className={`text-xs font-medium ${style.text} opacity-75`}>{fieldName}</p>
            )}
            <p className={`text-sm font-semibold ${style.text}`}>
              Status: {explanation.status}
            </p>
          </div>
        </div>
        {expandable && (
          isExpanded 
            ? <ChevronUp className={`w-4 h-4 ${style.icon}`} />
            : <ChevronDown className={`w-4 h-4 ${style.icon}`} />
        )}
      </div>
      
      {/* Reason (always visible) */}
      <p className={`text-xs mt-1 ${style.text}`}>
        <span className="font-medium">Reason:</span> {explanation.reason}
      </p>
      
      {/* Expandable details */}
      {(isExpanded || !expandable) && (
        <div className="mt-2 space-y-2 text-xs">
          {/* Requirements */}
          {explanation.requirements && explanation.requirements.length > 0 && (
            <div>
              <p className={`font-medium ${style.text}`}>Required to generate:</p>
              <ul className={`list-disc list-inside ml-1 ${style.text} opacity-90`}>
                {explanation.requirements.map((req, idx) => (
                  <li key={idx}>{req}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* How to rectify */}
          {explanation.rectify && explanation.rectify.length > 0 && (
            <div>
              <p className={`font-medium ${style.text}`}>How to rectify:</p>
              <ul className={`list-disc list-inside ml-1 ${style.text} opacity-90`}>
                {explanation.rectify.map((action, idx) => (
                  <li key={idx}>{action}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Inline Missing Data Text
 * 
 * Ultra-compact inline text for use within tables or tight spaces.
 * Shows status and reason in a single line with tooltip for details.
 */
export const InlineMissingData = ({
  fieldId,
  conditionKey,
  className = '',
}) => {
  const explanation = getFieldExplanation(fieldId, conditionKey);
  if (!explanation) return <span className="text-gray-400 text-xs">N/A</span>;
  
  const formatted = formatExplanation(explanation);
  
  return (
    <span 
      className={`text-xs text-gray-500 italic ${className}`}
      title={`${explanation.reason}\n\nRequired: ${formatted.requirements}\n\nHow to rectify: ${formatted.rectify}`}
      data-testid={`inline-missing-${fieldId}`}
    >
      {formatted.compact}
    </span>
  );
};

/**
 * PDF Export Format
 * 
 * Returns a compact string suitable for PDF exports.
 */
export const getPdfExplanation = (fieldId, conditionKey) => {
  const explanation = getFieldExplanation(fieldId, conditionKey);
  if (!explanation) return 'Unavailable';
  return `${explanation.status} (${explanation.reason.toLowerCase()})`;
};

/**
 * Wrapper component that shows content OR explanation
 * 
 * Props:
 * - hasData: boolean - Whether the field has data
 * - fieldId: string - Field identifier for explanation
 * - conditionKey: string - Condition key for explanation
 * - children: ReactNode - Content to show when data exists
 * - fieldName: string - Display name for the field (optional)
 * - compact: boolean - Use compact display (default: false)
 */
export const DataOrExplanation = ({
  hasData,
  fieldId,
  conditionKey,
  children,
  fieldName = null,
  compact = false,
  className = '',
}) => {
  if (hasData) {
    return <>{children}</>;
  }
  
  return (
    <MissingDataExplanation
      fieldId={fieldId}
      conditionKey={conditionKey}
      fieldName={fieldName}
      compact={compact}
      className={className}
    />
  );
};

export default MissingDataExplanation;
